import { RouteRecordRaw, RouterView } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { SigmaReportId } from '@/models/reports';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n, { loadTranslations } from '@/plugins/i18n';

import { toNonVueRoute } from './utils';

const ScheduleReports = () => import('@/pages/Reports/Schedule/ScheduleReports.vue');
const SellerStatus = () => import('@/pages/Reports/SellerStatus/SellerStatusOverview.vue');
const BuyerReports = () => import('@/pages/Reports/BuyerReports/BuyerReports.vue');
const BuyerMetrics = () => import('@/pages/Reports/BuyerMetrics/BuyerMetrics.vue');
const BuyerReplenishments = () => import('@/pages/Reports/BuyerReplenishments/BuyerReplenishments.vue');
const AdvertisingReport = () => import('@/pages/Reports/Advertising/AdvertisingReport.vue');
const InventoryInsights = () => import('@/pages/Reports/InventoryInsights/InventoryInsights.vue');
const SigmaReport = () => import('@/components/SigmaReport/SigmaReport.vue');
const SellerReports = () => import('@/pages/Reports/SellerReports/SellerReports.vue');

async function beforeEnter() {
  await loadTranslations('reports');
  await loadTranslations('company');
}

const reportsRoutes: RouteRecordRaw[] = [
  // Legacy
  toNonVueRoute({ path: 'reports/brand-owner/', name: ROUTE_NAMES.COMPANY_BRAND_OWNER_REPORTS }),
  toNonVueRoute({ path: 'reports/orders/', name: ROUTE_NAMES.ORDERS_REPORT }),
  toNonVueRoute({ path: 'reports/purchasing-insights/', name: ROUTE_NAMES.PURCHASING_INSIGHTS }),
  toNonVueRoute({ path: 'retailer-dashboard/', name: ROUTE_NAMES.RETAILER_DASHBOARD }),
  toNonVueRoute({ path: 'sales-dashboard/', name: ROUTE_NAMES.SALES_DASHBOARD }),
  toNonVueRoute({ path: 'reports/products/:productId/performance/', name: ROUTE_NAMES.PRODUCT_PERFORMANCE_REPORT }),
  toNonVueRoute({ path: 'reports/products/sold/', name: ROUTE_NAMES.PRODUCTS_SOLD_REPORT }),
  toNonVueRoute({ path: 'reports/brand-performance-report/', name: ROUTE_NAMES.BRAND_PERFORMANCE }),
  toNonVueRoute({ path: 'reports/downloads/', name: ROUTE_NAMES.REPORT_DOWNLOADS }),
  toNonVueRoute({ path: 'reports/insights/tableau/fulfillment/', name: ROUTE_NAMES.REPORT_FULFILLMENT_TABLEAU }),
  toNonVueRoute({ path: 'reports/accounts-receivable/', name: ROUTE_NAMES.ACCOUNTS_RECEIVABLE_REPORT }),
  toNonVueRoute({ path: 'reports/orders/payments/', name: ROUTE_NAMES.ORDER_PAYMENTS_REPORT }),
  toNonVueRoute({ path: 'reports/inventory-overview/', name: ROUTE_NAMES.INVENTORY_OVERVIEW_REPORT }),
  toNonVueRoute({ path: 'reports/customers/backordered-orders/', name: ROUTE_NAMES.BACKORDERED_ORDERS_REPORT }),
  toNonVueRoute({ path: 'reports/products/reserved-inventory/', name: ROUTE_NAMES.RESERVED_INVENTORY_REPORT }),
  toNonVueRoute({ path: 'reports/product-line/status/', name: ROUTE_NAMES.PRODUCT_LINES_REPORT }),
  toNonVueRoute({ path: 'reports/sales/team/summary/', name: ROUTE_NAMES.TEAM_SALES_REPORT }),
  toNonVueRoute({ path: 'reports/sales/activity-report/', name: ROUTE_NAMES.SALES_REPRESENTATIVE_ACTIVITY_REPORT }),
  toNonVueRoute({ path: 'reports/sales/team/tasks/', name: ROUTE_NAMES.SALES_REPRESENTATIVE_TASK_REPORT }),
  toNonVueRoute({ path: 'reports/sales/team/details/', name: ROUTE_NAMES.SALES_REPRESENTATIVE_ACTIVITY_FEED }),
  toNonVueRoute({ path: 'reports/customers/last-order-placed/', name: ROUTE_NAMES.CUSTOMER_LAST_ORDER_REPORT }),
  toNonVueRoute({ path: 'reports/customers/customer-orders/', name: ROUTE_NAMES.CUSTOMER_VALUE_REPORT }),
  toNonVueRoute({ path: 'reports/customers/customer-locations/', name: ROUTE_NAMES.CUSTOMER_LOCATIONS_REPORT }),
  // Vue

  // Buyer
  {
    path: 'buyer-reports',
    name: ROUTE_NAMES.BUYER_REPORTS,
    component: BuyerReports,
    meta: {
      title: () => i18n.t('reports.buyer.title'),
      description: () => i18n.t('reports.buyer.description'),
      permissions: [
        { rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.buyer)] },
      ],
    },
    beforeEnter,
  },
  {
    path: 'reports/buyer-metrics',
    name: ROUTE_NAMES.BUYER_METRICS,
    component: BuyerMetrics,
    beforeEnter,
    meta: {
      title: () => i18n.t('reports.retailer.title'),
      permissions: [
        {
          rules: [
            'canAccessRetailerInsights',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.buyer),
          ],
          redirect: (to) => ({ name: 'buyer-reports', params: { company: to.params.company } }),
        },
      ],
    },
  },
  {
    path: 'reports/buyer-replenishments',
    name: ROUTE_NAMES.BUYER_REPLENISHMENTS,
    component: BuyerReplenishments,
    beforeEnter,
    meta: {
      title: () => i18n.t('reports.replenishment.title'),
      permissions: [
        {
          rules: ['canAccessReplenishmentReportingTableau'],
          redirect: (to) => ({ name: 'buyer-reports', params: { company: to.params.company } }),
        },
      ],
    },
  },
  {
    path: 'reports/inventory-insights',
    name: ROUTE_NAMES.INVENTORY_INSIGHTS,
    component: InventoryInsights,
    beforeEnter: async () => {
      await beforeEnter();
      await loadTranslations('shop');
    },
    meta: {
      title: () => i18n.t('reports.buyer.inventoryInsights.title'),
      permissions: [
        {
          rules: [
            'canAccessCatmanReports',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.buyer),
          ],
        },
      ],
    },
  },
  {
    path: 'reports/category-management-insights',
    name: ROUTE_NAMES.CATEGORY_MANAGEMENT_INSIGHTS,
    component: SigmaReport,
    beforeEnter,
    props: {
      type: SigmaReportId.InventoryInsightsReport,
    },
    meta: {
      title: () => i18n.t('reports.categoryManagementInsights.title'),
      permissions: [
        {
          rules: [
            'canAccessCatmanReports',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.buyer),
          ],
        },
      ],
    },
  },
  // Seller
  {
    path: 'reports/',
    name: ROUTE_NAMES.REPORTS,
    component: () =>
      useUserStore().getIsSplitEnabled('llm_vue_seller_reports') ? SellerReports() : Promise.resolve(RouterView),
    meta: {
      title: () => i18n.t('reports.seller.title'),
      description: () => i18n.t('reports.seller.description'),
      permissions: [
        {
          rules: [
            'canAccessSalesReports',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
          ],
        },
      ],
      hasServerRenderedContent: () => !useUserStore().getIsSplitEnabled('llm_vue_seller_reports'),
    },
    beforeEnter,
  },
  {
    path: 'reports/advertising',
    name: ROUTE_NAMES.REPORTS_ADVERTISING,
    component: AdvertisingReport,
    beforeEnter,
    meta: {
      title: () => i18n.t('reports.advertising.title'),
      permissions: [
        {
          rules: [
            'canAccessAdsReports',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
          ],
        },
      ],
    },
  },
  {
    path: 'reports/advertising-insights',
    name: ROUTE_NAMES.REPORTS_ADVERTISING_INSIGHTS,
    component: SigmaReport,
    beforeEnter,
    props: {
      type: SigmaReportId.AdvertisingReport,
    },
    meta: {
      title: () => i18n.t('reports.advertisingInsights.title'),
      permissions: [
        {
          rules: ['canAccessAdsReports', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
        },
      ],
    },
  },
  {
    path: 'reports/business-insights',
    name: ROUTE_NAMES.REPORTS_BUSINESS_INSIGHTS,
    component: SigmaReport,
    beforeEnter,
    props: {
      type: SigmaReportId.PaymentsReport,
    },
    meta: {
      title: () => i18n.t('reports.businessInsights.title'),
      permissions: [
        {
          rules: [
            'canAccessMybiReport',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
          ],
        },
      ],
    },
  },
  {
    path: 'reports/market-insights',
    name: ROUTE_NAMES.REPORTS_MARKET_INSIGHTS,
    component: SigmaReport,
    beforeEnter,
    props: {
      type: SigmaReportId.MarketScapeReport,
    },
    meta: {
      title: () => i18n.t('reports.marketInsights.title'),
      permissions: [
        {
          rules: [
            'canAccessMarketscapeReport',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
          ],
        },
      ],
    },
  },
  {
    path: 'reports/schedule',
    name: ROUTE_NAMES.REPORT_SCHEDULE,
    component: ScheduleReports,
    beforeEnter,
    meta: {
      title: () => i18n.t('reports.scheduled.title'),
      description: () => i18n.t('reports.scheduled.description'),
      permissions: [
        {
          rules: [
            'canAccessSalesReports',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
          ],
        },
      ],
    },
  },
  {
    path: 'reports/seller-status',
    name: ROUTE_NAMES.SELLER_STATUS,
    component: SellerStatus,
    meta: {
      title: () => i18n.t('trustedSeller.sellerStatus.title'),
      permissions: [
        {
          rules: [
            'canAccessSalesReports',
            ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId && userActiveCompany.seller),
          ],
        },
      ],
    },
  },
  // shared between buyer and seller
  {
    path: 'reports/payment-on-sell-through-insights',
    name: ROUTE_NAMES.REPORTS_PAYMENT_ON_SELL_THROUGH_INSIGHTS,
    component: SigmaReport,
    beforeEnter,
    props: {
      type: SigmaReportId.PaymentOnSellThroughReport,
    },
    meta: {
      title: () => i18n.t('reports.paymentOnSellThroughInsights.title'),
      permissions: [
        {
          rules: ['canManageSellThrough', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
        },
      ],
    },
  },
  {
    path: 'reports/buyers-club-insights',
    name: ROUTE_NAMES.BUYERS_CLUB_INSIGHTS,
    component: SigmaReport,
    beforeEnter,
    props: {
      type: SigmaReportId.BuyersClubInsights,
    },
    meta: {
      title: () => i18n.t('reports.buyersClubInsights.title'),
      permissions: [
        {
          rules: [
            'canAccessSalesReports',
            ({ userActiveCompany, isSplitEnabled }) =>
              Boolean(userActiveCompany?.staffId) &&
              Boolean(userActiveCompany?.enableAdvancedReportingSigmaGpo) &&
              isSplitEnabled('llm_sigma_gpo_report'),
          ],
        },
      ],
    },
  },
];

export default reportsRoutes;
